import { type ComponentType, forwardRef, memo } from 'react';
import { useSelector } from 'react-redux';

import { ViewFieldSupport } from '@air/api/types';

import { type CustomFieldTableHeaderProps } from '~/components/TableView/CustomFieldTableHeader/types';
import { NameHeader } from '~/components/TableView/NameHeader';
import { SortableHeader } from '~/components/TableView/SortableHeader';
import { StaticHeader } from '~/components/TableView/StaticHeader';
import TableHeader from '~/components/TableView/TableHeader';
import { useTableView } from '~/components/TableView/TableViewContext';
import { makeActiveTableColumnFromOption } from '~/components/TableView/tableColumnWidthsUtils';
import { type ConfigVisibilityToggleType, getVisibilityToggleTypeFromString } from '~/constants/configViews';
import {
  DATE_CREATED_HEADER,
  DATE_MODIFIED_HEADER,
  FILE_HEADER,
  RESOLUTION_HEADER,
  TYPE_HEADER,
} from '~/constants/testIDs';
import { visibleConfigViewOptionsSelector } from '~/store/configViews/selectors';

export const dataTestIdMap: Record<ConfigVisibilityToggleType, string> = {
  boardCount: '',
  openCommentCount: '',
  tagCount: '',
  dateCreated: DATE_CREATED_HEADER,
  dateModified: DATE_MODIFIED_HEADER,
  ext: FILE_HEADER,
  size: 'size-header',
  type: TYPE_HEADER,
  resolution: RESOLUTION_HEADER,
  titleAndMetadata: '',
  dateTaken: 'dateTaken',
  dateUploaded: 'dateUploaded',
  uploadedBy: 'uploadedBy',
  views: 'views',
  downloads: 'downloads',
};

export type TableHeadersProps = {
  CustomFieldTableHeaderComponent: ComponentType<CustomFieldTableHeaderProps>;
  horizontalPadding: number;
  topPos: number;
};

const _TableHeaders = forwardRef<HTMLDivElement, TableHeadersProps>(
  ({ CustomFieldTableHeaderComponent, horizontalPadding, topPos }: TableHeadersProps, ref) => {
    const visibleConfigViewOptions = useSelector(visibleConfigViewOptionsSelector);
    const { getHeaderResizeHandle, getColumnWidthStyle, getTableWidthStyle } = useTableView();

    return (
      <TableHeader
        ref={ref}
        style={{
          top: topPos,
        }}
        innerStyle={{
          paddingLeft: horizontalPadding,
          paddingRight: horizontalPadding,
          ...getTableWidthStyle(),
        }}
      >
        <NameHeader />
        {visibleConfigViewOptions.map((option) => {
          const optionColumn = makeActiveTableColumnFromOption(
            option.fieldName,
            option.isCustomField,
            option.customFieldId,
          );

          if (option.isCustomField && option.customFieldId && option.label !== undefined) {
            return (
              <CustomFieldTableHeaderComponent
                key={option.customFieldId}
                customFieldName={option.label}
                style={getColumnWidthStyle(optionColumn)}
                resizeHandle={getHeaderResizeHandle(optionColumn)}
              />
            );
          }
          const toggleType = getVisibilityToggleTypeFromString(option.fieldName);

          if (toggleType) {
            const toggleSortBy = option.fieldName;

            if (toggleSortBy && option.supports.includes(ViewFieldSupport.sort)) {
              return (
                <SortableHeader
                  key={option.fieldName}
                  title={option.label}
                  sortingType={option.fieldName}
                  data-testid={dataTestIdMap[toggleType]}
                  style={getColumnWidthStyle(optionColumn)}
                  resizeHandle={getHeaderResizeHandle(optionColumn)}
                />
              );
            } else {
              return (
                <StaticHeader
                  key={option.fieldName}
                  title={option.label}
                  data-testid={dataTestIdMap[toggleType]}
                  style={getColumnWidthStyle(optionColumn)}
                  resizeHandle={getHeaderResizeHandle(optionColumn)}
                />
              );
            }
          }

          return null;
        })}
      </TableHeader>
    );
  },
);

_TableHeaders.displayName = '_TableHeaders';

export const TableHeaders = memo(_TableHeaders);

TableHeaders.displayName = 'PrivateTableHeaders';
